import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "../TextField/TextField";
import { ReactComponent as MagnifyingGlass } from "common-assets/assets/icons/magnifyingGlass.svg";
import { ReactComponent as CloseIcon } from "common-assets/assets/icons/closeX.svg";
import { ButtonBase } from "@material-ui/core";
import { SearchFieldModel } from "./tools/SearchField.model";
import { useUniqueWcagValues } from "common-react";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden";
import { Icon } from "../../Icon/Icon";

import useStyles from "./SearchField.style";

const AUTO_FILTER_ANNOUNCEMENT_DELAY = 300;

const SearchFieldClearButton = ({
  onClear,
  value,
  clearButtonAriaLabel,
}: {
  onClear: () => void;
  value: unknown;
  clearButtonAriaLabel: string;
}) => {
  const isFilled = !!(value && value !== "");
  const c = useStyles({ isFilled });
  return (
    <ButtonBase
      onClick={(e) => {
        e.stopPropagation();
        onClear();
      }}
      className={c.clearIconButton}
      tabIndex={isFilled ? 0 : -1}
      aria-hidden={isFilled ? false : true}
      aria-label={clearButtonAriaLabel}
    >
      <Icon>
        <CloseIcon  className={c.clearIcon} />
      </Icon>
    </ButtonBase>
  );
};

export const SearchField = ({
  onClear,
  value,
  size,
  clearButtonAriaLabel,
  autoFilterDescription,
  onFocus,
  onBlur,
  resultsCount,
  resultsAriaLabel,
  autoTestId,
  ...props
}: SearchFieldModel) => {
  const c = useStyles({ size });
  const { t } = useTranslation();
  const { getUniqueValue } = useUniqueWcagValues();
  const resultsId = resultsAriaLabel ? getUniqueValue('search-results') : undefined;

  const [debouncedResultsCount, setDebouncedResultsCount] = useState(resultsCount);
  const [autoFilterAnnouncement, setAutoFilterAnnouncement] = useState('');
  const autoFilterAnnouncementTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (resultsCount === undefined) return;

    const timer = setTimeout(() => {
      setDebouncedResultsCount(resultsCount);
    }, 500);

    return () => clearTimeout(timer);
  }, [resultsCount]);

  useEffect(() => () => {
    if (autoFilterAnnouncementTimerRef.current) {
      clearTimeout(autoFilterAnnouncementTimerRef.current);
    }
  }, []);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onFocus?.(event);

    if (!autoFilterDescription) return;

    if (autoFilterAnnouncementTimerRef.current) {
      clearTimeout(autoFilterAnnouncementTimerRef.current);
    }
    setAutoFilterAnnouncement('');
    autoFilterAnnouncementTimerRef.current = setTimeout(() => {
      setAutoFilterAnnouncement(autoFilterDescription);
      autoFilterAnnouncementTimerRef.current = null;
    }, AUTO_FILTER_ANNOUNCEMENT_DELAY);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onBlur?.(event);
    if (autoFilterAnnouncementTimerRef.current) {
      clearTimeout(autoFilterAnnouncementTimerRef.current);
      autoFilterAnnouncementTimerRef.current = null;
    }
    setAutoFilterAnnouncement('');
  };

  return (
    <>
      <TextField
        {...props}
        value={value}
        size={size}
        onFocus={handleFocus}
        onBlur={handleBlur}
        inputProps={{
          ...props.inputProps,
          'data-autotestid': autoTestId,
        }}
        startAdornment={
          <MagnifyingGlass
            aria-hidden={true}
            focusable={false}
            className={c.magnifyingGlass}
          />
        }
        endAdornment={
          onClear ? (
            <SearchFieldClearButton
              onClear={onClear}
              value={value}
              clearButtonAriaLabel={clearButtonAriaLabel}
            />
          ) : null
        }
      />

      {autoFilterDescription && (
        <output
          aria-live="polite"
          aria-atomic="true"
          data-testid="search-auto-filter-announcement"
        >
          {autoFilterAnnouncement && (
            <VisuallyHidden>{autoFilterAnnouncement}</VisuallyHidden>
          )}
        </output>
      )}

      {resultsAriaLabel && resultsId && debouncedResultsCount !== undefined && (
        <div
          id={resultsId}
          aria-live="polite"
          aria-atomic="true"
          role="status"
        >
          <VisuallyHidden>
            {t(resultsAriaLabel, { count: debouncedResultsCount })}
          </VisuallyHidden>
        </div>
      )}
    </>
  );
};
